import type { PackItemSpec } from "../scenario/types";

/**
 * Rich "what's actually there" detail card for a tapped pack-room object.
 * Replaces the old one-line inspect panel: tapping the sticky note shows the
 * actual handwritten password, tapping the USB shows the real file browser
 * with the client folder on it, tapping the tablet shows the mixed
 * personal/work home screen. Every illustration is a pure CSS/DOM mock — no
 * photographic asset dependency, so it ships in the same deploy as the code.
 */

const FILE_GLYPH: Record<string, string> = {
  sheet: "XLS",
  doc: "DOC",
  pdf: "PDF",
  archive: "ZIP",
  text: "TXT",
  image: "IMG",
};

function Illustration({ item }: { item: PackItemSpec }) {
  const r = item.reveal;
  if (!r) return null;

  if (r.kind === "sticky_note") {
    const lines = r.noteLines ?? [];
    return (
      <div className="pir-stage pir-stage-note">
        <div className="pir-note">
          <span className="pir-note-tape" aria-hidden />
          {lines.map((ln, i) => (
            <div key={i} className="pir-note-line">
              {ln}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (r.kind === "file_explorer") {
    const files = r.files ?? [];
    return (
      <div className="pir-stage pir-stage-explorer">
        <div className="pir-explorer">
          <div className="pir-explorer-bar">
            <span className="pir-explorer-dot red" aria-hidden />
            <span className="pir-explorer-dot amber" aria-hidden />
            <span className="pir-explorer-dot green" aria-hidden />
            <span className="pir-explorer-title">{r.explorerTitle ?? "USB Drive (E:)"}</span>
          </div>
          <div className="pir-explorer-path">This PC &gt; {r.explorerTitle ?? "USB Drive (E:)"}</div>
          <div className="pir-explorer-list">
            {files.map((f, i) => (
              <div key={i} className={`pir-file ${f.sensitive ? "sensitive" : ""}`}>
                <span className={`pir-file-ico k-${f.kind ?? "text"}`} aria-hidden>
                  {FILE_GLYPH[f.kind ?? "text"] ?? "TXT"}
                </span>
                <span className="pir-file-name">{f.name}</span>
                <span className="pir-file-size">{f.size ?? ""}</span>
              </div>
            ))}
          </div>
          <div className="pir-explorer-status">{files.length} items — no password required</div>
        </div>
      </div>
    );
  }

  if (r.kind === "lock_screen") {
    return (
      <div className="pir-stage pir-stage-lock">
        <div className="pir-lock">
          <div className="pir-lock-badge" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
          </div>
          <div className="pir-lock-label">{r.deviceLabel ?? item.label}</div>
          {r.deviceCaption && <div className="pir-lock-caption">{r.deviceCaption}</div>}
        </div>
      </div>
    );
  }

  if (r.kind === "device_home") {
    const apps = r.homeApps ?? [];
    return (
      <div className="pir-stage pir-stage-home">
        <div className="pir-home">
          {r.homeBanner && <div className="pir-home-banner">{r.homeBanner}</div>}
          <div className="pir-home-grid">
            {apps.map((a, i) => (
              <div key={i} className={`pir-home-app tone-${a.tone ?? "personal"}`}>
                <span className="pir-home-app-ico" aria-hidden />
                <span className="pir-home-app-label">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // product
  return (
    <div className="pir-stage pir-stage-product">
      <div className="pir-product">
        <div className="pir-product-glow" aria-hidden />
        <div className="pir-product-name">{r.title ?? item.label}</div>
        <div className="pir-product-specs">
          {(r.specs ?? []).map((s, i) => (
            <span key={i} className="pir-chip">
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PackItemReveal({
  item,
  packed,
  canToggle,
  onToggle,
  onClose,
}: {
  item: PackItemSpec;
  packed: boolean;
  canToggle: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const r = item.reveal;
  const trap = item.role === "trap";
  const kicker = r?.kicker ?? (trap ? "LEAVE THIS BEHIND" : item.role === "safe" ? "PACK THIS" : "TAKE A LOOK");

  // Packing/unpacking should read as a decision, not a silent toggle: every
  // action closes the card straight after so the click has visible effect
  // (2026-07-14: fixed an unrelated pointer-events bug that made these
  // buttons dead-on-click; while here, also make "don't pack this" its own
  // explicit choice for traps instead of just "close and hope").
  const packThenClose = () => {
    if (!packed) onToggle();
    onClose();
  };
  const leaveThenClose = () => {
    if (packed) onToggle();
    onClose();
  };

  return (
    <div className="pir-overlay" role="dialog" aria-label={item.label} onClick={onClose}>
      <div className="pir-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pir-x" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className={`pir-kicker ${trap ? "trap" : item.role === "safe" ? "safe" : ""}`}>{kicker}</div>
        <h3 className="pir-title">{r?.title ?? item.label}</h3>
        {r ? <Illustration item={item} /> : null}
        <p className="pir-narrative">{r?.narrative ?? item.detail ?? ""}</p>
        {canToggle && trap && (
          <div className="pir-action-row">
            <button type="button" className="btn pir-action pir-action-primary" onClick={leaveThenClose}>
              {packed ? "Take it back out — leave it behind" : "Leave it behind"}
            </button>
            <button type="button" className="btn ghost pir-action pir-action-ghost" onClick={packThenClose}>
              Pack it anyway
            </button>
          </div>
        )}
        {canToggle && !trap && (
          <button type="button" className={`btn pir-action ${packed ? "on" : ""}`} onClick={packed ? leaveThenClose : packThenClose}>
            {packed ? "Remove from the bag" : "Pack it"}
          </button>
        )}
      </div>
    </div>
  );
}
